import { HttpEvent, HttpEventType, HttpHeaders, HttpParams, HttpProgressEvent } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Sort } from '@angular/material/sort';

import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { PaginationData, PaginationRange } from 'tiime-material';

import { ApiCommonService } from '@api-services/api-common.service';
import { API_HEADERS, COMPANY_API_ROUTE_BASE } from '@constants/api-routes.constant';
import { ApiAlertError } from '@decorators/api-alert-error';
import { DocumentType } from '@enums/document-type.enum';
import { HttpHelper } from '@helpers/http.helper';
import { DocumentsFilters } from '@interfaces/documents-filters';
import { Document, DocumentJson } from '@models/document';
import { DocumentCategory, DocumentCategoryJson } from '@models/document-category';
import { DocumentTransactionMatching, DocumentTransactionMatchingJson } from '@models/document-transaction-matching';
import { Label } from '@models/label';
import { LinkedTransaction, LinkedTransactionJson } from '@models/linked-transaction';
import { Transaction } from '@models/transaction';
import { TemporaryEncoder } from '@modules/core/temporary-encoder';
import { FileSaverData } from '@services/file.service';

/**
 * DocumentApiService Service.
 */
@Injectable({
  providedIn: 'root'
})
export class DocumentApiService extends ApiCommonService<Document> {
  resourceUrl = `${COMPANY_API_ROUTE_BASE}/documents`;

  fromJson(json: DocumentJson): Document {
    return Document.fromJson(json);
  }

  toJson(document: Document): Partial<DocumentJson> {
    return Document.toJson(document);
  }

  @ApiAlertError()
  getDocumentPreview(documentId: number): Observable<Blob> {
    const url = `${this.resourceUrl}/${documentId}/preview`;
    return this.http.get(url, { responseType: 'blob' });
  }

  @ApiAlertError()
  getDocumentFile(documentId: number): Observable<Blob> {
    const url = `${this.resourceUrl}/${documentId}/file`;
    return this.http.get(url, { responseType: 'blob' });
  }

  @ApiAlertError()
  downloadDocument(documentId: number): Observable<FileSaverData> {
    const url = `${this.resourceUrl}/${documentId}/file`;
    const blobObservable = this.http.get(url, {
      responseType: 'blob',
      observe: 'response'
    });
    return HttpHelper.getFileSaverData(blobObservable);
  }

  @ApiAlertError()
  getDocuments(
    range: PaginationRange,
    searchTerms: string,
    sort: Sort,
    filters?: DocumentsFilters,
    expand?: string
  ): Observable<PaginationData<Document>> {
    let params = new HttpParams({ encoder: new TemporaryEncoder() });
    params = HttpHelper.setQParam(searchTerms, params);
    params = HttpHelper.setSortParam(sort, params);

    if (filters?.categoryId) {
      params = HttpHelper.setParam('document_categories', filters.categoryId.toString(), params);
    }

    if (typeof filters?.hasLabel === 'boolean') {
      params = HttpHelper.setParam('has_label', filters.hasLabel.toString(), params);
    }

    if (typeof filters?.withExpenseReport === 'boolean') {
      params = HttpHelper.setParam('linked_to_expense_report', filters.withExpenseReport.toString(), params);
    }

    if (filters?.incomplete) {
      params = HttpHelper.setParam('incomplete', filters.incomplete.toString(), params);
    }

    if (filters?.types) {
      params = HttpHelper.setParam('types', filters.types.join(','), params);
    }

    if (filters?.matchable !== undefined) {
      if (filters.matchable) {
        // Hide accountant owner categories for matching
        params = HttpHelper.setParam('hidden', 'false', params);
      }
      params = HttpHelper.setParam('matchable', filters.matchable.toString(), params);
    }

    if (typeof filters?.matched === 'boolean') {
      params = HttpHelper.setParam('linked_entities_has_bank_transactions', filters.matched.toString(), params);
    }

    if (typeof filters?.accountable === 'boolean') {
      params = HttpHelper.setParam('accountable', filters.accountable.toString(), params);
    }

    if (expand?.length > 0) {
      params = HttpHelper.setExpandParam(expand, params);
    }

    return super.getPaginated(range, params);
  }

  @ApiAlertError()
  getDocument(documentId: number, expand?: string): Observable<Document> {
    let params = HttpHelper.createHttpParams();

    if (expand?.length > 0) {
      params = HttpHelper.setExpandParam(expand, params);
    }
    return super.getById(documentId, undefined, { params });
  }

  uploadReceiptDocument(file: File): Observable<Document | HttpProgressEvent> {
    const url = `${COMPANY_API_ROUTE_BASE}/receipts`;
    const formData = new FormData();
    formData.append('file', file);

    return this.http
      .post(url, formData, {
        reportProgress: true,
        observe: 'events'
      })
      .pipe(
        filter(
          (event: HttpEvent<DocumentJson>) =>
            event.type === HttpEventType.UploadProgress || event.type === HttpEventType.Response
        ),
        map((event: HttpEvent<DocumentJson>) =>
          event.type === HttpEventType.Response ? new Document() : (event as HttpProgressEvent)
        )
      );
  }

  uploadDocument(documentCategoryId: number, file: File): Observable<Document | HttpProgressEvent> {
    const url = `${COMPANY_API_ROUTE_BASE}/document_categories/${documentCategoryId}/documents`;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('source', 'accountant');

    return this.http
      .post(url, formData, {
        reportProgress: true,
        observe: 'events'
      })
      .pipe(
        filter(
          (event: HttpEvent<DocumentJson>) =>
            event.type === HttpEventType.UploadProgress || event.type === HttpEventType.Response
        ),
        map((event: HttpEvent<DocumentJson>) =>
          event.type === HttpEventType.Response ? new Document() : (event as HttpProgressEvent)
        )
      );
  }

  @ApiAlertError()
  deleteDocument(documentId: number): Observable<void> {
    const url = `${this.resourceUrl}/${documentId}`;

    return this.http.delete<void>(url);
  }

  @ApiAlertError()
  getDocumentCategories(): Observable<DocumentCategory[]> {
    const url = `${COMPANY_API_ROUTE_BASE}/document_categories`;

    return this.http
      .get(url)
      .pipe(
        map((documentCategoriesJson: DocumentCategoryJson[]) =>
          documentCategoriesJson
            .filter((documentCategoryJson: DocumentCategoryJson) => documentCategoryJson.id !== null)
            .map((documentCategoryJson: DocumentCategoryJson) => DocumentCategory.fromJson(documentCategoryJson))
        )
      );
  }

  @ApiAlertError()
  createDocumentCategory(name: string): Observable<DocumentCategory> {
    const url = `${COMPANY_API_ROUTE_BASE}/document_categories`;
    const body = {
      name
    };

    return this.http
      .post(url, body)
      .pipe(map((documentCategoryJson: DocumentCategoryJson) => DocumentCategory.fromJson(documentCategoryJson)));
  }

  @ApiAlertError()
  deleteDocumentCategory(documentCategoryId: number): Observable<void> {
    const url = `${COMPANY_API_ROUTE_BASE}/document_categories/${documentCategoryId}`;

    return this.http.delete<void>(url);
  }

  @ApiAlertError()
  matchDocumentWithTransactions(
    documentId: number,
    transactions: Transaction[],
    label?: Label
  ): Observable<DocumentTransactionMatching[]> {
    const headers = new HttpHeaders({
      Accept: API_HEADERS.documentTransactionsMatchingsV2
    });
    const url = `${this.resourceUrl}/${documentId}/bank_transaction_matchings`;
    const body = {
      bank_transactions: transactions.map(transaction => ({ id: transaction.id })),
      label: label ?? undefined
    };
    return this.http
      .put(url, body, { headers })
      .pipe(
        map((matchingsJson: DocumentTransactionMatchingJson[]) =>
          matchingsJson.map(matchingJson => DocumentTransactionMatching.fromJson(matchingJson))
        )
      );
  }

  @ApiAlertError()
  updateAdvancedExpense(document: Document): Observable<Document> {
    const url = `${COMPANY_API_ROUTE_BASE}/advanced_expenses/${document.id}`;

    return this.update(document.id, document, url);
  }

  @ApiAlertError()
  getLinkedTransactions(documentId: number, type: DocumentType): Observable<LinkedTransaction[]> {
    const url = `${this.resourceUrl}/${documentId}/bank_transactions?type=${type}`;

    return this.http
      .get<LinkedTransactionJson[]>(url)
      .pipe(
        map((jsons: LinkedTransactionJson[]) =>
          jsons.map((json: LinkedTransactionJson) => LinkedTransaction.fromJson(json))
        )
      );
  }

  @ApiAlertError()
  updateDocument(document: Document): Observable<Document> {
    return this.update(document.id, document);
  }
}
